// src/lib/supabase.ts
// SUPABASE ACTIVO — nube de NIIZ.
// Cliente creado con las credenciales de .env (VITE_SUPABASE_URL + ANON_KEY).

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/types/supabase';

export type TypedSupabaseClient = SupabaseClient<Database>;

const supabaseUrl = import.meta.env['VITE_SUPABASE_URL'] as string | undefined;
const supabaseAnonKey = import.meta.env['VITE_SUPABASE_ANON_KEY'] as string | undefined;

/** ¿Está Supabase configurado en .env con valores reales? */
export const isSupabaseConfigured = (): boolean =>
  Boolean(supabaseUrl && supabaseUrl.startsWith('https://') && supabaseAnonKey && supabaseAnonKey.startsWith('eyJ'));

// Cliente único. null si no hay credenciales válidas (la app opera en modo local).
export const supabase: TypedSupabaseClient | null =
  isSupabaseConfigured() && supabaseUrl && supabaseAnonKey
    ? createClient<Database>(supabaseUrl, supabaseAnonKey)
    : null;

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────
export const isCloudActive = (): boolean => !!supabase;

export const getCurrentUser = async () => {
  if (!supabase) return null;
  const { data, error } = await supabase.auth.getUser();
  if (error) {
    console.warn('Sesión expirada o inválida:', error.message);
    return null;
  }
  return data.user;
};

export const signOut = async () => {
  if (!supabase) return;
  try {
    await supabase.auth.signOut();
  } catch (e) {
    console.error('Error al cerrar sesión en nube:', e);
  } finally {
    localStorage.removeItem('niiz-supabase-auth');
  }
};

// ─────────────────────────────────────────────────────────────
// LOGGING DE DESARROLLO
// ─────────────────────────────────────────────────────────────
if (import.meta.env.DEV) {
  if (supabase) {
    console.info('%c✅ Supabase conectado (nube activa)', 'background:#10b981;color:white;padding:4px 8px;border-radius:4px;font-weight:bold;');
  } else {
    console.warn('%c⚠️ Supabase no configurado (modo local)', 'background:#f59e0b;color:black;padding:4px 8px;border-radius:4px;font-weight:bold;');
    console.log('   Configure VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY en .env para habilitar la nube.');
  }
}

// ─────────────────────────────────────────────────────────────
// RE-EXPORTS
// ─────────────────────────────────────────────────────────────
export type {
  Session,
  User,
  AuthChangeEvent,
  AuthError,
  PostgrestError,
  RealtimePostgresChangesPayload
} from '@supabase/supabase-js';
